import React, {
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import {
  Alerts,
  AlertsContext,
  Button,
  Form,
  FormContext,
  FormErrorTypes,
  googlePlaceToAddress,
  useServerResponseTranslation,
} from "dash-ui";
import { useTranslation } from "react-i18next";

import {
  BrokerFragment,
  useUpdateBrokerMutation,
  GetBrokerByIdDocument,
  GetBrokersDocument,
  UpdateBrokerInCmsInput,
} from "gql/generated.graphql";

import ChangesConfirmationModal from "containers/properties/components/ChangesConfirmationModal/ChangesConfirmationModal";

import LocationSection from "./components/LocationSection/LocationSection";
import AgencySection from "./components/AgencySection/AgencySection";
import StatusesSection from "./components/StatusesSection/StatusesSection";

import { isBrokerEqualFormState } from "./BrokerInfoForm.helpers";
import { StyledForm } from "./BrokerInfoForm.styles";
import { useValidateBrokerInfoForm } from "./BrokerInfoForm.hooks";
import { FormFields } from "../../BrokerDetailsPage.constants";

const BrokerInfoForm = ({ broker }: { broker?: BrokerFragment }) => {
  const validate = useValidateBrokerInfoForm();
  const { t } = useTranslation("pages");
  const { setFormValues } = useContext(FormContext);
  const { toggleAlert } = useContext(AlertsContext);
  const formRef = useRef<HTMLFormElement | null>(null);
  const { formState, formOnSubmit, formOnError, formOnSuccess } =
    useContext(FormContext);
  const [updateBroker, { loading: updateBrokerLoading }] =
    useUpdateBrokerMutation({
      refetchQueries: [
        {
          query: GetBrokerByIdDocument,
          variables: {
            brokerId: broker?._id,
          },
        },
        {
          query: GetBrokersDocument,
        },
      ],
      onCompleted: () => {
        formOnSuccess();
        toggleAlert(Alerts.ChangesSaved, true);
      },
      onError: (error) => formOnError(translateErrorMessage(error)),
    });
  const translateErrorMessage = useServerResponseTranslation();

  useEffect(() => {
    if (broker) {
      const {
        profile,
        account,
        accountSubscription,
        preferredPropertySettings,
        license,
        ...restBroker
      } = broker;

      setFormValues({
        firstName: profile.firstName,
        lastName: profile.lastName,
        avatarFileId: profile.avatarFile?._id,
        email: account.email,
        phoneNumber: {
          number: account.phone?.number,
          dialCode: account.phone?.dialCode,
        },
        place: {
          placeId: preferredPropertySettings?.location?.googlePlaceId,
          lat: preferredPropertySettings?.location?.geoLocation.coordinates[1],
          lng: preferredPropertySettings?.location?.geoLocation.coordinates[0],
        },
        radius: preferredPropertySettings?.radiusInMiles,
        agencyName: restBroker.agencyName,
        agencyWebsite: restBroker.agencyWebsite,
        subscriptionPlanName: accountSubscription?.subscriptionProduct.name,
        subscriptionType: restBroker.subscriptionType,
        demoExpirationDate: restBroker.demoExpirationDate,
        subscriptionPlanStatus: accountSubscription?.status,
        userStatus: account.status,
        isRealUser: broker.isRealUser,
        excludeFromReports: account.excludeFromReports,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [broker]);

  const submitForm = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();
      formOnSubmit();

      if (
        Object.values(FormFields).some(
          (field) => !validate(field, formState[field])
        )
      )
        return;

      const input: UpdateBrokerInCmsInput = {
        agencyName: formState.agencyName,
        agencyWebsite: formState.agencyWebsite,
        subscriptionType: formState.subscriptionType,
        demoExpirationDate: formState.demoExpirationDate,
        isRealUser: formState.isRealUser,
        excludeFromReports: formState.excludeFromReports,
        preferredPropertySettingsInput: {
          radiusInMiles: formState.radius,
          locationInput: Object.values(formState.place).filter(Boolean).length
            ? {
                googlePlaceId: formState.place.placeId,
                lat: formState.place.lat,
                lng: formState.place.lng,
                ...googlePlaceToAddress(formState.place),
              }
            : null,
        },
        profileInput: {
          firstName: formState.firstName,
          lastName: formState.lastName,
          avatarFileId: formState.avatarFileId,
        },
      };

      await updateBroker({
        variables: { brokerId: broker?._id, input: input },
      });
    },
    [broker, formState, formOnSubmit, updateBroker]
  );
  const isEqual = Boolean(broker && isBrokerEqualFormState(broker, formState));

  return (
    <>
      <StyledForm p={6} w="100%" ref={formRef} onSubmit={submitForm}>
        <StatusesSection />
        <AgencySection />
        {/* Shows only if the information is set, because we removed the fields from
        Broker Portal - and to reduce unnecessary Google Maps API calls */}
        {broker?.preferredPropertySettings && <LocationSection />}
        <Button
          onClick={() => {
            if (formRef.current) {
              formRef.current.requestSubmit();
            }
          }}
          disabled={updateBrokerLoading || isEqual}
          isLoading={updateBrokerLoading}
        >
          {t("pages:brokersPage.saveBrokerDetails")}
        </Button>
        <Form.Group>
          <Form.Feedback $errorType={FormErrorTypes.Invalid}>
            {formState.errorSlug}
          </Form.Feedback>
        </Form.Group>
      </StyledForm>
      <ChangesConfirmationModal condition={isEqual} />
    </>
  );
};

export default BrokerInfoForm;
